import React from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DOMPurify from 'dompurify';

/**
 * A reusable modal component for displaying information
 * @param {Object} props
 * @param {boolean} props.open - Controls the visibility of the modal
 * @param {function} props.onClose - Function to call when closing the modal
 * @param {string} props.title - Title to display in the modal header
 * @param {string|React.ReactNode} props.content - Content to display in the modal body
 * @param {boolean} props.sanitizeContent - Whether to sanitize HTML content (default: false)
 * @param {Object} props.contentProps - Additional props to pass to the content container
 * @param {Object} props.modalProps - Additional props to pass to the Modal component
 */
const InfoModal = ({
  open,
  onClose,
  title,
  content,
  sanitizeContent = false,
  contentProps = {},
  modalProps = {},
}) => {
  const theme = useTheme();

  const renderContent = () => {
    if (sanitizeContent && typeof content === 'string') {
      return (
        <Box
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(content),
          }}
          {...contentProps}
        />
      );
    }
    return typeof content === 'string' ? <Typography>{content}</Typography> : content;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="info-modal-title"
      aria-describedby="info-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      {...modalProps}
    >
      <Box
        sx={{
          position: 'relative',
          width: { xs: '90%', sm: '80%', md: '60%' },
          maxHeight: '60vh',
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'background.default',
          borderRadius: 2,
          boxShadow: theme.palette.mode === 'dark' 
            ? '0px 4px 20px rgba(0, 0, 0, 0.5)' 
            : '0 8px 32px rgba(0, 0, 0, 0.08)',
          p: { xs: 2, sm: 3 },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          mx: 2,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            pb: 2,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography 
            variant="h6" 
            component="h2"
            id="info-modal-title"
            sx={{
              fontWeight: 600,
              color: theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
            }}
          >
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="close"
            sx={{
              color: theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            overflow: 'auto',
            flex: 1,
            pr: 2,
            mr: -2,
            maxHeight: 'calc(60vh - 120px)',
            '&::-webkit-scrollbar': {
              width: '8px',
              borderRadius: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              borderRadius: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
              borderRadius: '8px',
              '&:hover': {
                background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
        >
          <Box
            id="info-modal-description"
            sx={{
              color: theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                color: theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
                margin: '0 0 1rem 0',
                fontWeight: 'bold',
                fontSize: {
                  xs: '1.25rem',
                  sm: '1.5rem',
                },
                lineHeight: 1.2,
              },
              '& p': {
                margin: '0 0 1rem 0',
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                },
                lineHeight: 1.6,
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
              },
              '& ul, & ol': {
                paddingLeft: 3,
                marginBottom: 2,
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
              },
              '& li': {
                marginBottom: 1,
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.primary',
              },
              '& a': {
                color: theme.palette.brand.main,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
              '& strong, & b': {
                fontWeight: 'bold',
                color: theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 1,
                margin: '1rem 0',
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                marginBottom: 2,
                color: theme.palette.mode === 'dark' ? 'common.white' : 'text.primary',
              },
              '& th, & td': {
                border: `1px solid ${theme.palette.divider}`,
                padding: 1,
                textAlign: 'left',
              },
              '& th': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                fontWeight: 'bold',
              },
              ...contentProps.sx,
            }}
          >
            {renderContent()}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default InfoModal; 